// @ts-strict-ignore
import React from 'react'
import { graphql, useLazyLoadQuery } from 'react-relay'
import { Button, Grid } from '@mui/material'
import { Delete, Refresh } from '@mui/icons-material'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { useSnackbar } from 'hooks/redux'
import { useFetchKey } from 'hooks/relay'
import { ReferencesEditor_RequestReferenceMutation } from '__generated__/ReferencesEditor_RequestReferenceMutation.graphql'
import { ReferencesEditor_DeleteReferenceMutation } from '__generated__/ReferencesEditor_DeleteReferenceMutation.graphql'
import { ReferencesEditor_ReferencesQuery } from '__generated__/ReferencesEditor_ReferencesQuery.graphql'
import Condition from 'components/Condition'
import ConfirmButton from 'components/ConfirmButton'
import TextField from 'components/TextField'
import { IAddReferenceFormValues } from './components/AddReferenceForm/useAddReferenceForm'
import AddReferenceForm from './components/AddReferenceForm'

function ReferencesEditor() {
  const { showSnackbarMessage } = useSnackbar()
  const [fetchKey, incrementFetchKey] = useFetchKey()

  const { execute: requestReference, loading } = useQuickCommit<ReferencesEditor_RequestReferenceMutation>(
    graphql`
      mutation ReferencesEditor_RequestReferenceMutation($input: RequestReferenceInput!) {
        requestReference(input: $input) {
          reference {
            user {
              references {
                name
                email
                status
              }
            }
          }
        }
      }
    `,
  )

  const { execute: deleteReference } = useQuickCommit<ReferencesEditor_DeleteReferenceMutation>(
    graphql`
      mutation ReferencesEditor_DeleteReferenceMutation($input: DeleteReferenceInput!) {
        deleteReference(input: $input) {
          reference {
            user {
              references {
                name
                email
              }
            }
          }
        }
      }
    `,
  )

  const { execute: resendReferenceRequest } = useQuickCommit<ReferencesEditor_DeleteReferenceMutation>(
    graphql`
      mutation ReferencesEditor_ResendReferenceRequestMutation($input: ResendReferenceRequestInput!) {
        resendReferenceRequest(input: $input) {
          reference {
            user {
              references {
                name
                email
              }
            }
          }
        }
      }
    `,
  )

  const referencesData = useLazyLoadQuery<ReferencesEditor_ReferencesQuery>(
    graphql`
      query ReferencesEditor_ReferencesQuery {
        currentUser {
          name
          references(status: pending) {
            id
            name
            email
          }
        }
      }
    `,
    {},
    {
      fetchPolicy: 'store-and-network',
      fetchKey: fetchKey,
    },
  )

  const handleSubmitReference = async (values: IAddReferenceFormValues) => {
    await requestReference({
      input: {
        name: `${values.firstName || ''} ${values.lastName || ''}`.trim(),
        email: values.email,
        message: values.message,
      },
    })

    showSnackbarMessage('Reference request sent', { level: 'info' })
    incrementFetchKey()
  }

  const handleDeleteReference = async (referenceId: string) => {
    await deleteReference({
      input: {
        referenceId: referenceId,
      },
    })

    showSnackbarMessage('Reference deleted', { level: 'info' })
    incrementFetchKey()
  }

  const handleResendReference = async (referenceId: string) => {
    await resendReferenceRequest({
      input: {
        referenceId: referenceId,
      },
    })

    showSnackbarMessage('Reference request sent', { level: 'info' })
    incrementFetchKey()
  }

  const references = referencesData?.currentUser?.references || []

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        Ask for <Condition condition={references.length > 0}>more</Condition> references to make your profile stronger.
      </Grid>

      <Grid item xs={12} />

      {references.map(reference => (
        <React.Fragment key={reference.id}>
          <Grid item xs={12} sm={4}>
            <TextField value={reference.name} label="Name" fullWidth disabled />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField value={reference.email} label="Email" fullWidth disabled />
          </Grid>

          <Grid item xs={12} sm={2}>
            <Button
              color="secondary"
              onClick={() => handleResendReference(reference.id)}
              fullWidth
              startIcon={<Refresh />}
            >
              Resend
            </Button>
          </Grid>

          <Grid item xs={12} sm={2}>
            <ConfirmButton
              color="error"
              dialogTitle="Delete Reference"
              dialogMessage="Delete this reference? This cannot be undone without sending the request again."
              onClick={() => handleDeleteReference(reference.id)}
              fullWidth
              startIcon={<Delete />}
            >
              Delete
            </ConfirmButton>
          </Grid>
        </React.Fragment>
      ))}

      <Grid item xs={12} />
      <Grid item xs={12} />

      <Grid item xs={12}>
        <AddReferenceForm
          key={fetchKey} // remounts the component after submit, causing the form to be cleared for a better UX
          loading={loading}
          profileName={referencesData?.currentUser?.name}
          submitReference={handleSubmitReference}
        />
      </Grid>
    </Grid>
  )
}

export default React.memo(ReferencesEditor)
